import { FC, useCallback, useEffect, useMemo } from "react";

import {
  advancedSearchCriteriasSelector,
  buildAdvancedFilterList,
  BusinessFilter,
  createAdvancedBusinessFilter,
  currentDataLocaleSelector,
  DataViewKey,
  getBusinessFiltersForCurrentLocale,
  sheetFieldsSelector,
  useDataView,
} from "@keepeek/commons";
import { KReportWidget, useThemeColors, withSuspense } from "@keepeek/refront-components";
import { useRecoilValue } from "recoil";

import { KIcon } from "../../../components/common/KIcon";
import logger from "../../../lib/logger-utils";
import { useBackContext } from "../../../providers/backContext/hooks";
import { useActiveFiltersFromURL } from "../../../providers/businessFilter/hooks/useActiveFiltersFromURL";
import { useElement } from "../../../providers/element/hooks";
import EditableSanitizeHtml from "../../FrontEdit/EditableSanitizeHtml";
import { WidgetUIState } from "../atoms";
import { useWidgetState } from "../hooks";
import { KWidgetProps } from "../models";

export type ReportWidgetProps = {
  widgetId: string;
  backgroundColor?: string;
  reportField?: string;
  fieldValue?: string;
} & KWidgetProps;

const ReportWidget: FC<ReportWidgetProps> = function ({
  widgetId,
  backgroundColor,
  reportField,
  fieldValue,
  type,
}) {
  const { setWidgetState, widgetState } = useWidgetState({
    id: widgetId,
    type,
  });
  const { convertColor } = useThemeColors();
  const { element, isUnitaryShareElement } = useElement();
  const { goToPageWithBackContext } = useBackContext();
  const dataLocale = useRecoilValue(currentDataLocaleSelector);

  const sheetFields = useRecoilValue(sheetFieldsSelector);
  const criterias = useRecoilValue(advancedSearchCriteriasSelector);

  const reportBusinessFilter = useMemo(() => {
    if (reportField) {
      const filterList: BusinessFilter[] = buildAdvancedFilterList(
        reportField ? reportField : "",
        sheetFields,
        criterias,
      );
      const filterListForCurrentLocale = getBusinessFiltersForCurrentLocale(filterList, dataLocale);

      const query =
        fieldValue ||
        element?.fields
          ?.find((element) => {
            return element.id == reportField;
          })
          ?.value.toString();
      if (query) {
        if (!filterListForCurrentLocale || filterListForCurrentLocale.length === 0) {
          logger.debug(
            `ReportWidget - can't find filter for field and locale`,
            reportField,
            dataLocale,
          );
          return [];
        }
        const reportFilter = createAdvancedBusinessFilter({
          fieldId: filterListForCurrentLocale[0].filter.id,
          query,
          filterLabel: filterListForCurrentLocale[0].filter.label,
          searchSeparator: "",
          ignoreQuerySplit: true,
          addQuotes: true,
        });
        return getBusinessFiltersForCurrentLocale([reportFilter], dataLocale);
      }
    }
    return [];
  }, [criterias, element?.fields, fieldValue, dataLocale, reportField, sheetFields]);

  const ignoreCurrentMediaFilter = createAdvancedBusinessFilter({
    fieldId: "id",
    query: `-${element?.id}`,
    filterLabel: "id",
  });

  const { elements } = useDataView({
    key: DataViewKey.REPORT,
    filters: reportBusinessFilter,
  });

  const { setActiveFilters } = useActiveFiltersFromURL();
  const handleClick = useCallback(
    (reportFilters: BusinessFilter[]) => {
      setActiveFilters(reportFilters);
    },
    [setActiveFilters],
  );

  useEffect(() => {
    if (
      !reportField ||
      (!element && !fieldValue) ||
      !element?.fields?.find((element) => {
        return element.id == reportField;
      }) ||
      elements.length == 0 ||
      isUnitaryShareElement
    ) {
      setWidgetState({ state: WidgetUIState.NULL });
    } else {
      setWidgetState({ state: WidgetUIState.RENDERED });
    }
  }, [setWidgetState, reportField, element, fieldValue, elements.length, isUnitaryShareElement]);

  if (widgetState.state === WidgetUIState.RENDERED) {
    return (
      <KReportWidget
        id={widgetId}
        backgroundColor={convertColor(backgroundColor)}
        onSeeAllReportClick={() => {
          // use business filters without ignoreCurrentMediaFilter
          handleClick(reportBusinessFilter);
        }}
        seeAllButtonLabel={
          <EditableSanitizeHtml labelKey={"home.widget.report.button-all.label"} />
        }
        filters={[...reportBusinessFilter, ignoreCurrentMediaFilter]}
        goToPageWithBackContext={goToPageWithBackContext}
        KIcon={KIcon}
      />
    );
  }
  return null;
};

export default withSuspense(ReportWidget);
